/** Modern-theme layout for ContactPage. State/submit logic lives in ContactPage. */
import { Link } from 'react-router-dom';
import { MessageSquare, CheckCircle2 } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { BackButton } from '../components/BackButton';

const ink = (n) => ({ color: `var(--am-ink-${n})` });

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="am-label block">{label}</label>
      {children}
    </div>
  );
}

function SentCard({ f, homePath, homeLabel }) {
  return (
    <div className="space-y-4" data-testid="contact-sent">
      <div className="flex items-start gap-3 p-4 rounded-[14px]" style={{ background: 'var(--am-emerald-50)', border: '1px solid var(--am-emerald-200)' }}>
        <CheckCircle2 className="w-6 h-6 shrink-0 mt-0.5" style={{ color: 'var(--am-emerald-600)' }} />
        <div>
          <p className="font-semibold" style={ink(1)}>Message received</p>
          <p className="text-sm mt-1" style={ink(2)}>
            Thanks {f.name.split(' ')[0]}. A dispatcher will reach out to <strong>{f.email}</strong>{f.phone ? <> or <strong>{f.phone}</strong></> : null}.
          </p>
          {f.ticketId && <p className="text-xs mt-2" style={ink(3)}>Reference ID: #{f.ticketId.slice(-8).toUpperCase()}</p>}
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <button type="button" onClick={f.resetForm} className="am-btn-ghost h-11" data-testid="contact-send-another">Send another</button>
        <Link to={homePath} className="am-cta h-11 px-5 inline-flex items-center justify-center text-sm" data-testid="contact-back-home">{homeLabel}</Link>
      </div>
    </div>
  );
}

function ContactForm({ f, homePath }) {
  return (
    <form onSubmit={f.handleSubmit} className="space-y-4" data-testid="contact-form">
      <Field label="Name *">
        <input type="text" value={f.name} onChange={(e) => f.setName(e.target.value)} required maxLength={120} disabled={f.loading} className="am-input" data-testid="contact-input-name" />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Email *">
          <input type="email" value={f.email} onChange={(e) => f.setEmail(e.target.value)} required disabled={f.loading} className="am-input" data-testid="contact-input-email" />
        </Field>
        <Field label="Phone">
          <input type="tel" value={f.phone} onChange={(e) => f.setPhone(e.target.value)} maxLength={40} disabled={f.loading} className="am-input" data-testid="contact-input-phone" />
        </Field>
      </div>
      <Field label="Subject *">
        <input type="text" value={f.subject} onChange={(e) => f.setSubject(e.target.value)} required maxLength={200} disabled={f.loading} placeholder="Order issue, product question, feedback…" className="am-input" data-testid="contact-input-subject" />
      </Field>
      <Field label="Message *">
        <textarea value={f.body} onChange={(e) => f.setBody(e.target.value)} required maxLength={5000} rows={6} disabled={f.loading}
          placeholder="Tell us what's going on. Include an order number if you have one."
          className="am-input" style={{ height: 'auto', padding: '12px 14px', resize: 'vertical' }} data-testid="contact-input-body" />
        <div className="text-right text-xs am-tnum" style={ink(3)}>{f.body.length}/5000</div>
      </Field>
      {f.error && <div className="text-sm p-3 rounded-[10px] bg-red-50 border border-red-200 text-red-700" data-testid="contact-error">{f.error}</div>}
      <div className="flex flex-col sm:flex-row gap-2 pt-1">
        <button type="submit" disabled={f.loading} className="am-cta flex-1 h-12 text-sm" data-testid="contact-submit">{f.loading ? 'Sending…' : 'Send message'}</button>
        <Link to={homePath} className="am-btn-ghost h-12" data-testid="contact-cancel">Cancel</Link>
      </div>
    </form>
  );
}

export function ContactPageModern({ f, isCustomer }) {
  const homePath = isCustomer ? '/customer' : '/';
  return (
    <div className="am-modern-scope min-h-screen">
      {isCustomer && <Navbar />}
      <div className="max-w-lg mx-auto px-4 py-6 sm:py-10">
        <div className="flex items-center gap-3 mb-5">
          <BackButton to={homePath} />
          <div>
            <p className="am-eyebrow">We're here to help</p>
            <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2" data-testid="contact-page-title">
              <MessageSquare className="w-6 h-6" style={{ color: 'var(--am-emerald-600)' }} />Contact dispatch
            </h1>
          </div>
        </div>
        <div className="am-card p-5 sm:p-6" data-testid="contact-page">
          <p className="text-sm mb-5" style={ink(3)}>We usually reply within an hour during operating hours.</p>
          {f.sent
            ? <SentCard f={f} homePath={homePath} homeLabel={isCustomer ? 'Back to shop' : 'Back to home'} />
            : <ContactForm f={f} homePath={homePath} />}
        </div>
      </div>
    </div>
  );
}
